import {Box, Typography, Switch, FormControlLabel, CircularProgress} from '@mui/material'
import moment from 'moment'
import React, {useState, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {splitTimeSlotsByTimeOfTheDay} from '../timeUtils'
import {TimeSlotPickerProps} from '../types'

import {TimeSlotSection} from './TimeSlotSection'

/**
 * Time slot picker component adapted from mobile app HMTimeSlotPicker
 * Renders time slots in a grid layout with Morning/Afternoon/Evening sections
 */
export const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  initialTime,
  allTimeSlots,
  onSelectedTimeChange,
  isLoading
}) => {
  const {t} = useTranslation()
  const [onlyShowAvailableSlots, setOnlyShowAvailableSlots] = useState(false)
  const [shouldShowOffHoursSlots, setShouldShowOffHoursSlots] = useState(false)

  // Check if we have both instant approval and non-instant approval slots
  const {hasAtLeastOneAvailableSlot, hasAtLeastOneUnavailableSlot, hasOutOfBusinessHoursSlots} =
    useMemo(
      () => ({
        hasAtLeastOneAvailableSlot: allTimeSlots.some((slot) => slot.isAvailable),
        hasAtLeastOneUnavailableSlot: allTimeSlots.some((slot) => !slot.isAvailable),
        hasOutOfBusinessHoursSlots: allTimeSlots.some((slot) => slot.isOutsideLoadHours)
      }),
      [allTimeSlots]
    )
  const shouldShowInstantApprovalToggle = hasAtLeastOneAvailableSlot && hasAtLeastOneUnavailableSlot
  const shouldShowOffHoursToggle = hasOutOfBusinessHoursSlots

  // Categorize slots by time of day
  const {
    Morning: morningSlots,
    Afternoon: afternoonSlots,
    Evening: eveningSlots
  } = useMemo(
    () => splitTimeSlotsByTimeOfTheDay(allTimeSlots.filter((slot) => !slot.isOutsideLoadHours)),
    [allTimeSlots]
  )

  const {
    Morning: morningOffHoursSlots,
    Afternoon: afternoonOffHoursSlots,
    Evening: eveningOffHoursSlots
  } = useMemo(
    () => splitTimeSlotsByTimeOfTheDay(allTimeSlots.filter((slot) => slot.isOutsideLoadHours)),
    [allTimeSlots]
  )

  // Normalize initial time - allow any time within business hours to be selected
  const initialDateNormalized = useMemo(() => {
    if (!initialTime) return moment()

    return moment(initialTime)
  }, [initialTime])

  // Handle time slot selection
  const handleTimeSlotSelect = useCallback(
    (slot: string) => {
      if (!initialTime) return

      const selectedTime = moment(initialTime).clone()
      const [hours, minutes] = slot.split(':').map(Number)
      const newDateTime = selectedTime.hours(hours).minutes(minutes).seconds(0)

      onSelectedTimeChange(newDateTime)
    },
    [initialTime, onSelectedTimeChange]
  )

  // Handle instant approval toggle change
  const handleToggleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setOnlyShowAvailableSlots(event.target.checked)
  }, [])

  // Handle off-hours toggle change
  const handleOffHoursToggleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldShowOffHoursSlots(event.target.checked)
  }, [])

  // Get current selected time slot
  const selectedTimeSlot = initialDateNormalized.format('HH:mm')

  return (
    <Box sx={{width: '100%', position: 'relative'}} data-test-id="time-slot-picker">
      {/* Show only available slots toggle */}
      {shouldShowInstantApprovalToggle && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px'
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={onlyShowAvailableSlots}
                onChange={handleToggleChange}
                sx={{
                  '& .MuiSwitch-track': {
                    backgroundColor: '#D4DBE0'
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: '#FFFFFF'
                  }
                }}
                data-test-id="show-only-available-slots-toggle"
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#00274D'
                }}
              >
                {t('orderIntake.rmc.steps.time.showInstantApprovalsOnly')}
              </Typography>
            }
            sx={{margin: 0}}
          />
        </Box>
      )}

      {/* Time slot sections */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        <TimeSlotSection
          title={t('orderIntake.rmc.steps.time.morning')}
          slots={morningSlots}
          selectedTimeSlot={selectedTimeSlot}
          onTimeSlotSelect={handleTimeSlotSelect}
          onlyShowAvailableSlots={onlyShowAvailableSlots}
        />

        <TimeSlotSection
          title={t('orderIntake.rmc.steps.time.afternoon')}
          slots={afternoonSlots}
          selectedTimeSlot={selectedTimeSlot}
          onTimeSlotSelect={handleTimeSlotSelect}
          onlyShowAvailableSlots={onlyShowAvailableSlots}
        />

        <TimeSlotSection
          title={t('orderIntake.rmc.steps.time.evening')}
          slots={eveningSlots}
          selectedTimeSlot={selectedTimeSlot}
          onTimeSlotSelect={handleTimeSlotSelect}
          onlyShowAvailableSlots={onlyShowAvailableSlots}
        />
      </Box>
      {/* Show off-hours slots toggle */}
      {shouldShowOffHoursToggle && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px'
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={shouldShowOffHoursSlots}
                onChange={handleOffHoursToggleChange}
                sx={{
                  '& .MuiSwitch-track': {
                    backgroundColor: '#D4DBE0'
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: '#FFFFFF'
                  }
                }}
                data-test-id="show-off-hours-slots-toggle"
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#00274D'
                }}
              >
                {t('orderIntake.rmc.steps.time.displayOffHoursTimeSlots')}
              </Typography>
            }
            sx={{margin: 0}}
          />
        </Box>
      )}
      {/* Time slot sections */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        {shouldShowOffHoursSlots && (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <TimeSlotSection
              title={t('orderIntake.rmc.steps.time.morning')}
              slots={morningOffHoursSlots}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={handleTimeSlotSelect}
              onlyShowAvailableSlots={onlyShowAvailableSlots}
            />

            <TimeSlotSection
              title={t('orderIntake.rmc.steps.time.afternoon')}
              slots={afternoonOffHoursSlots}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={handleTimeSlotSelect}
              onlyShowAvailableSlots={onlyShowAvailableSlots}
            />

            <TimeSlotSection
              title={t('orderIntake.rmc.steps.time.evening')}
              slots={eveningOffHoursSlots}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={handleTimeSlotSelect}
              onlyShowAvailableSlots={onlyShowAvailableSlots}
            />
          </Box>
        )}
      </Box>
      {/* Loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'center',
            borderRadius: '8px',
            zIndex: 10
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
    </Box>
  )
}
